import axios, { AxiosPromise, AxiosError } from "axios";
import Cookie from "js-cookie";
import { REST_URL, COOKIE_TOKEN, COOKIE_LANG } from "customGlobal";
import { TransactionResponse } from "interfaces/Scenario";
import auth, { updateToken } from "auth";
import { getUIContext } from "./common";
import { UserSettingProps } from "types/UserSettings";

/**
 * Création de l'api XHR avec une url de base & l'utilisateur de connexion pour l'api
 */
const api = axios.create({
  baseURL: REST_URL
});

api.interceptors.request.use(
  async config => {
    await updateToken();
    config.headers.Authorization = `Bearer ${auth.token}`;
    config.headers["Accept-Language"] = Cookie.get(COOKIE_LANG);
    return config;
  },
  (error: AxiosError<any>) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  res => res,
  (error: AxiosError<any>) => {
    if (error.response && error.response.status === 401) {
      // redirectToAuth();
      return;
    }

    return Promise.reject(error);
  }
);

/**
 * Permet de récupérer tous les contextes
 */
export function getAllContext() {
  const params = getUIContext();
  return api.get(`/cb/context?${params}`);
}

/**
 * Permet de récupérer un context particulier
 * @param id id de contexte
 */
export function getContext(id: string | number) {
  const params = getUIContext();
  return api.get(`/cb/context/${id}?${params}`);
}

/**
 * Permet de récupérer la liste des transactions disponible pour le contexte sélectionné
 * @param contextCode code du contexte sélectionné
 */
export function getTransactionByContext(contextCode: string | number) {
  const params = getUIContext();
  return api.get(`/cb/context/${contextCode}/transaction?${params}`);
}

/**
 * Permet de récupérer une transaction particuliere
 * @param code code de transaction
 */
export function getTransaction(code: string) {
  const params = getUIContext();
  return api.get(`/cb/transaction/${code}?${params}`);
}

/**
 * Permet de récupérer les étapes du scenario
 * @param transactionCode code de transaction
 */
export function getScenario(
  transactionCode: string
): AxiosPromise<{
  machine: Record<string, any>;
  context: Record<string, any>;
  errorMessage: string;
}> {
  const params = getUIContext();
  return api.get(`/cb/transaction/${transactionCode}/definition?${params}`);
}

/**
 * Permet d'initialiser une étape d'un scénario
 * @param scenarioId id de scenario
 * @param etapeId id de l'étape
 * @param data données déjà présente
 */
export function initValues(
  scenarioId: string | number,
  etapeId: string | number,
  data: Record<string, any>,
  defaultValue: string,
  defaultValueSource: string,
  name: string[]
) {
  const params = getUIContext();
  return api.post(`/cb/transaction/${scenarioId}/init/${etapeId}?${params}`, {
    data,
    defaultValue,
    defaultValueSource,
    name
  });
}

export function getRecap(
  scenarioId: string,
  target: string,
  data: Record<string, any>
): AxiosPromise<{ recap: string }> {
  const params = getUIContext();
  return api.post(`/cb/transaction/${scenarioId}/recap?${params}`, { target, data });
}
/**
 * Permet de lancer la validation d'une étape de scénario
 * @param transactionId id de transaction
 * @param transactionStepId id de l'étape
 * @param data donnée présente
 * @param index index si on est dans une boucle
 */
export function validateStep(
  transactionId: string | number,
  transactionStepId: string | number,
  data: { [key: string]: any },
  index?: number
): AxiosPromise<TransactionResponse> {
  const request = {
    transactionId,
    transactionStepId,
    data,
    index
  };

  const params = getUIContext();
  return api.post(`/cb/validate/step?${params}`, request);
}

/**
 * Permet de lancer la validation de la transaction
 * @param transactionId id de transaction
 * @param transactionStepId id de l'étape finale
 * @param data donnée présente
 */
export function validateTransaction(
  transactionId: string | number,
  transactionStepId: string | number,
  data: { [key: string]: any }
): AxiosPromise<string> {
  const request = {
    transactionId,
    transactionStepId,
    data
  };

  const params = getUIContext();
  return api.post(`/cb/validate/transaction?${params}`, request);
}

/**
 * Permet de récupérer des informations complémentaire au contexte dans lequel on se trouve
 * @param contextCode code du contexte
 */
export function homeInformation(contextCode?: string | number) {
  if (contextCode) {
    const params = getUIContext();
    return api.get(`/cb/context/${contextCode}/info?${params}`).then(res => res.data);
  } else {
    return Promise.resolve("");
  }
}

/**
 * Appel de récupération des colonnes de la table de recherche avancé
 * @param transactionStepId id de l'étape
 */
export function getSearchColumns(transactionStepId: string | number) {
  const params = getUIContext();
  return api.get(`/cb/transaction/search/${transactionStepId}/columns?${params}`);
}

/**
 * Appel de récupérations des données de la table de recherche avancé
 * @param transactionStepId id de l'étape
 * @param first début de la pagination
 */
export function getSearchData(transactionStepId: string | number, first?: number) {
  const searchParam = getUIContext();
  if (first) {
    searchParam.append("first", first.toString());
  }

  let search = searchParam.toString();

  return api.get(
    `/cb/transaction/search/${transactionStepId}/data${search.length > 0 ? `?${search}` : ""}`
  );
}

export function getTranslation(url: string): AxiosPromise<Record<string, string>> {
  const params = getUIContext();
  return api.get(`/translation/${url}?${params}`);
}

/**
 * Récupère la liste des langues disponibles
 */
export function fetchLangs(): AxiosPromise<{ value: string; label: string; baseValue: string }[]> {
  const params = getUIContext();
  return api.get(`/userSettings/langue?${params}`);
}

/**
 * Met à jour la langue sélectionnée
 * @param lang Code de la langue à définir
 */
export function updateSelectedLang(lang: string): AxiosPromise<void> {
  const params = getUIContext();
  return api.put(`/userSettings/langue?${params}`, { langueCode: lang });
}

/**
 * Récupère les paramètres utilisateur
 */
export function fetchUserSettings(): AxiosPromise<UserSettingProps> {
  const params = getUIContext();
  return api.get(`/userSettings?${params}`);
}
